/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import { liteClient } from 'algoliasearch/lite';
import {
  InstantSearch,
  Configure,
  Index,
  useMenu,
  useStats,
  useInstantSearch,
} from 'react-instantsearch';
import {
  Grid,
  GridCol,
  GridRow,
  Heading,
  Section,
  Text,
  VerticalSpace,
} from '@arc-ui/components';

import { ArcSearchInput, ResultsHeaderLive } from '../../components/molecules';
import { ResultsArea } from '../../components/organisms';
import { routingConfig } from '../../utils/routing';
import { HITS_PER_PAGE } from '../../constants';
import { useDeviceType } from '../../hooks';
import { useEnvVariable } from '../../contexts/EnvironmentContext';

const SearchContent: React.FC = () => {
  const { isMobile, isTablet } = useDeviceType();
  const { items: facetBuckets } = useMenu({ attribute: 'category' });
  const { nbHits } = useStats();
  const { indexUiState } = useInstantSearch();
  const trimmedQuery = (indexUiState?.query || '').trim();

  return (
    <>
      <VerticalSpace size={isMobile && trimmedQuery ? '20' : '64'} />
      <Grid isGutterless>
        {!trimmedQuery && (
          <>
            <GridRow>
              <GridCol
                xs={12}
                s={12}
                m={12}
                l={6}
                xl={6}
                offset={{ l: 3, xl: 3 }}
              >
                <ArcSearchInput />
              </GridCol>
            </GridRow>
            <GridRow align="center" justify="center" direction="column">
              <VerticalSpace size="40" />
              <Heading color="brand" align="center" size="l">
                Start your search
              </Heading>
              <VerticalSpace size="16" />
              <Text size="m" align="center">
                Enter a keyword above to find products, services, or support
                content.
              </Text>
              <VerticalSpace size="40" />
            </GridRow>
          </>
        )}
        {trimmedQuery && (
          <>
            {isMobile || isTablet ? (
              <>
                <GridRow align="center" justify="center">
                  <GridCol
                    xs={12}
                    s={12}
                    m={12}
                    align={{ xs: 'center', s: 'center' }}
                  >
                    <Heading
                      level="1"
                      size="m"
                      align="center"
                      id="search-heading"
                    >
                      Search results
                    </Heading>
                  </GridCol>
                </GridRow>
                <GridRow>
                  <GridCol xs={12} s={12} m={12}>
                    <VerticalSpace size="20" />
                    <ResultsHeaderLive
                      facetBuckets={facetBuckets}
                      nbHits={nbHits}
                      align="center"
                    />
                    <VerticalSpace size="20" />
                  </GridCol>
                </GridRow>
                <GridRow>
                  <GridCol xs={12} s={12} m={12}>
                    <ArcSearchInput />
                  </GridCol>
                </GridRow>
              </>
            ) : (
              <GridRow align="center" justify="center">
                <GridCol
                  xs={12}
                  s={12}
                  m={12}
                  l={3}
                  xl={4}
                  align={{ xs: 'center', s: 'center' }}
                >
                  <Heading level="1" size="m" align="left" id="search-heading">
                    Search results
                  </Heading>
                </GridCol>
                <GridCol xs={12} s={12} m={12} l={9} xl={8}>
                  <ArcSearchInput />
                </GridCol>
              </GridRow>
            )}
          </>
        )}
      </Grid>
      <VerticalSpace size="28" />
      <ResultsArea />
    </>
  );
};

const SearchPage: React.FC = () => {
  const { viteAlgoliaIndexName, viteAlgoliaAppId, viteAlgoliaSearchKey } =
    useEnvVariable();
  const searchClient = useMemo(
    () => liteClient(viteAlgoliaAppId, viteAlgoliaSearchKey),
    [],
  );

  const routing = useMemo(() => routingConfig(viteAlgoliaIndexName), []);

  return (
    <main id="site-search" role="main">
      <Section>
        <InstantSearch
          searchClient={searchClient}
          routing={routing}
          future={{ preserveSharedStateOnUnmount: true }}
          data-testid="instant-search"
          insights={true}
        >
          <Configure hitsPerPage={HITS_PER_PAGE} />
          <Index indexName={viteAlgoliaIndexName}>
            <SearchContent />
          </Index>
        </InstantSearch>
      </Section>
    </main>
  );
};

export default SearchPage;
